/**
 * 入庫 · 逐項貨品編輯器（新增發貨單用；`ui-spec` 頁 4）
 *
 * ⚠️ **cbm 公式唔喺前端**：每箱 cbm 係後端（`@coeldery/sku-schema`）計好嘅值，
 *    呢度只係「箱數 × 每箱 cbm」嘅顯示估算（最終以建立後 API 回嘅 `declaredCbm` 為準）。
 */
import { useTranslation } from 'react-i18next';
import FormSelectField from '@coeldery/form-select-field';
import FormTextField from '@coeldery/form-text-field';
import { btnGhost, hint as hintStyle } from '@coeldery/ui-kit';
import type { Item } from '../lib/api';

export interface IntakeDraftLine {
  /** 前端臨時 id（用嚟 key ＋ 刪除） */
  key: string;
  itemId: string;
  declaredCartons: string;
  productionDate: string;
  expiryDate: string;
}

export const blankLine = (): IntakeDraftLine => ({
  key: `l-${Math.random().toString(36).slice(2, 9)}`,
  itemId: '',
  declaredCartons: '',
  productionDate: '',
  expiryDate: '',
});

export interface IntakeLinesEditorProps {
  lines: IntakeDraftLine[];
  items: Item[];
  onChange: (lines: IntakeDraftLine[]) => void;
}

export default function IntakeLinesEditor({ lines, items, onChange }: IntakeLinesEditorProps) {
  const { t } = useTranslation();

  const itemOptions = items.map((it) => ({
    value: it.id,
    label: `${it.name}（${it.barcode}）`,
  }));

  const patch = (key: string, field: keyof IntakeDraftLine, value: string | null) =>
    onChange(lines.map((l) => (l.key === key ? { ...l, [field]: value ?? '' } : l)));

  const estimatedCbm = (l: IntakeDraftLine): string => {
    const it = items.find((i) => i.id === l.itemId);
    const cartons = Number(l.declaredCartons);
    if (!it || !Number.isFinite(cartons) || cartons < 1) return '—';
    return String(Math.round(cartons * it.cbmPerCarton * 1e6) / 1e6);
  };

  return (
    <div style={{ display: 'grid', gap: '12px' }}>
      {lines.map((l, i) => (
        <div
          key={l.key}
          style={{
            border: '1px solid var(--color-divider)',
            borderRadius: '12px',
            padding: '12px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <strong style={{ fontSize: '14px' }}>
              {t('intake.item')} {i + 1} · {estimatedCbm(l)} cbm
            </strong>
            {lines.length > 1 && (
              <button
                type="button"
                style={{ ...btnGhost, minHeight: '34px' }}
                onClick={() => onChange(lines.filter((x) => x.key !== l.key))}
              >
                {t('intake.removeLine')}
              </button>
            )}
          </div>

          <div style={{ display: 'grid', gap: '10px', marginTop: '8px' }}>
            <FormSelectField
              id={`f-line-item-${l.key}`}
              labelKey="intake.pickItem"
              value={l.itemId}
              onChange={(v) => patch(l.key, 'itemId', v)}
              options={[{ value: '', label: '—' }, ...itemOptions]}
            />
            <FormTextField
              id={`f-line-cartons-${l.key}`}
              labelKey="intake.declaredCartons"
              type="number"
              value={l.declaredCartons}
              onChange={(v) => patch(l.key, 'declaredCartons', v)}
            />
            <FormTextField
              id={`f-line-prod-${l.key}`}
              labelKey="intake.productionDate"
              type="date"
              value={l.productionDate}
              onChange={(v) => patch(l.key, 'productionDate', v)}
            />
            <FormTextField
              id={`f-line-exp-${l.key}`}
              labelKey="intake.expiryDate"
              type="date"
              value={l.expiryDate}
              onChange={(v) => patch(l.key, 'expiryDate', v)}
            />
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button type="button" style={btnGhost} onClick={() => onChange([...lines, blankLine()])}>
          {t('intake.addLine')}
        </button>
      </div>
      <p style={hintStyle}>{t('master.hintCbm')}</p>
    </div>
  );
}
